import type { ReactNode } from "react";
import { normalizeEntityType } from "../../types";
import type { EntityType } from "../../types";
import { isFlight } from "../../utils/tripDisplay";
import "./TypeIcon.css";

/**
 * Colour family for a type. Similar things share a tint, so a day's timeline
 * reads at a glance: rides are blue, food is amber, stays are plum, shows are
 * coral, parks are green and travel is teal. Plain places stay neutral.
 */
/**
 * A place type, or "CUSTOM" for a name-only activity. A type saved before the
 * October 2026 merge (a string like "LOUNGE") shows its new type's icon.
 */
export type TypeIconType = EntityType | "CUSTOM" | (string & {}) | null | undefined;

export type TypeTone = "ride" | "park" | "stay" | "food" | "show" | "travel" | "plain";

type Glyph =
  | "globe" | "wheel" | "bed" | "ship" | "tower" | "coaster" | "ticket"
  | "dining" | "star" | "bus" | "plane" | "pin" | "pencil";

const GLYPHS: Record<Glyph, ReactNode> = {
  globe: <><circle cx="12" cy="12" r="9" /><path d="M3 12h18M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18" /></>,
  wheel: <><circle cx="12" cy="10" r="6.5" /><path d="M12 3.5v13M5.5 10h13M7.4 5.4l9.2 9.2M16.6 5.4l-9.2 9.2M9 21l3-11 3 11M7 21h10" /></>,
  bed: <><path d="M3 19V6M3 14h18v5M21 14a3 3 0 0 0-3-3h-7v3" /><circle cx="7" cy="11.5" r="1.5" /></>,
  ship: <path d="M3 16l2 4h14l2-4zM5 16v-5h14v5M8 11V7h8v4M12 4v3" />,
  tower: <path d="M9 21l1-10h4l1 10M7 7h10l-1 4H8zM12 3v4M7 21h10" />,
  coaster: <path d="M2 20h20M2 14c4 0 4-9 8-9s4 11 8 11c2 0 3-2 4-3M6 20v-7M10 20V5M14 20v-8M18 20v-4" />,
  ticket: <path d="M3 7h18v3a2 2 0 0 0 0 4v3H3v-3a2 2 0 0 0 0-4zM15 7v2M15 11v2M15 15v2" />,
  dining: <path d="M7 3v8M4 3v5a3 3 0 0 0 6 0V3M7 11v10M17 3c-2 2-3 5-3 8h3v10" />,
  star: <path d="M12 3l2.3 5.5L20 9l-4.3 3.9L17 19l-5-3-5 3 1.3-6.1L4 9l5.7-.5z" />,
  bus: <><rect x="4" y="4" width="16" height="13" rx="2" /><path d="M4 11h16M8 17v3M16 17v3" /></>,
  plane: <path d="M10.5 21l1.5-1 1.5 1v-1.5L12 18v-4.5l8 2.5v-2l-8-4.5V5a1 1 0 0 0-2 0v4.5l-8 4.5v2l8-2.5V18l-1.5 1.5z" />,
  pin: <><path d="M12 21s-7-6-7-11a7 7 0 0 1 14 0c0 5-7 11-7 11z" /><circle cx="12" cy="10" r="2.5" /></>,
  pencil: <path d="M4 20l4-1L19 8l-3-3L5 16zM14 7l3 3" />,
};

function glyphFor(type: TypeIconType, name?: string | null): [Glyph, TypeTone] {
  if (type === "CUSTOM") return ["pencil", "plain"];
  const current = normalizeEntityType(type);
  if (!current) return ["pin", "plain"];
  if (isFlight(current, name)) return ["plane", "travel"];
  switch (current) {
    case "DESTINATION": return ["globe", "park"];
    case "PARK": return ["wheel", "park"];
    case "EVENT": return ["star", "show"];
    case "HOTEL": return ["bed", "stay"];
    case "SHIP": return ["ship", "travel"];
    case "AIRPORT": return ["tower", "travel"];
    case "ATTRACTION": return ["coaster", "ride"];
    case "EXPERIENCE": return ["ticket", "show"];
    case "RESTAURANT": return ["dining", "food"];
    case "TRANSPORTATION": return ["bus", "travel"];
    case "LOCATION": return ["pin", "plain"];
    default: return ["pin", "plain"];
  }
}

interface TypeIconProps {
  type: TypeIconType;
  /** The place's name, so a flight gets a plane rather than a bus. */
  name?: string | null;
  /**
   * Fill the parent with the type's tint (for round or square icon slots).
   * Without it the glyph sits inline at the surrounding text size.
   */
  tile?: boolean;
}

/** The icon for a place type: a line glyph tinted by its colour family. */
export function TypeIcon({ type, name, tile }: TypeIconProps) {
  const [glyph, tone] = glyphFor(type, name);
  return (
    <span className={tile ? "type-icon type-icon-tile" : "type-icon"} data-tone={tone} aria-hidden="true">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        {GLYPHS[glyph]}
      </svg>
    </span>
  );
}
